import { Link } from "react-router";
import { Adjuntos, Alerta, Badge, Button, Casilla, Combobox, DataList, Desplegable, Dialog, MontoField, PercentField, Row, Select, SelectorArchivos, Spinner, Stack, TextArea, TextField } from "@/components";
import { ACCEPT_ADJUNTO } from "@/lib/adjuntos";
import { DialogosPasos, PasosSubcategoria } from "./PasosSubcategoria";
import type { Cotizacion, EstadoCotizacion } from "@/lib/api";
import {
  ETIQUETA_ESTADO,
  formatDiferenciaPorcentaje,
  formatPuntaje,
  OPCIONES_COMPOSICION,
  OPCIONES_FILTRO_ESTADO,
  textoCantidad,
  textoSubcategoria,
  TONO_ESTADO,
  TONO_ETAPA,
  type FilaComparacion,
  type NodoSubcategoria,
} from "@/lib/cotizaciones";
import { formatFecha, formatMoneda } from "@/lib/format";
import { formatPorcentajeCuota } from "@/lib/formaDePago";
import { formatCantidad } from "@/lib/presupuesto";
import { DialogosCategorias, EdicionArbol } from "./EdicionCategorias";
import { useCotizacionesTab } from "./useCotizacionesTab";

function Monto({ c }: { c: Cotizacion }) {
  if (c.monto === null || c.moneda === null) return <span className="text-texto-suave">Sin monto</span>;
  return (
    <Stack espacio={1}>
      <span>{formatMoneda(c.monto, c.moneda)}</span>
      {c.moneda === "ARS" && c.monto_usd !== null && <span className="text-chico text-texto-suave">{formatMoneda(c.monto_usd, "USD")}</span>}
    </Stack>
  );
}

export function CotizacionesTab() {
  const t = useCotizacionesTab();
  const d = t.dialogo;

  const columnas = [
    { clave: "fecha", titulo: "Fecha", render: (c: Cotizacion) => formatFecha(c.fecha) },
    {
      clave: "proveedor",
      titulo: "Proveedor",
      render: (c: Cotizacion) => (
        <Link to={`/cotizaciones/${c.id}`} className="text-primario underline-offset-2 hover:underline">
          {c.proveedor}
        </Link>
      ),
    },
    { clave: "descripcion", titulo: "Descripción", render: (c: Cotizacion) => c.descripcion ?? "—" },
    { clave: "monto", titulo: "Monto", render: (c: Cotizacion) => <Monto c={c} /> },
    { clave: "estado", titulo: "Estado", render: (c: Cotizacion) => <Badge tono={TONO_ESTADO[c.estado]}>{ETIQUETA_ESTADO[c.estado]}</Badge> },
    {
      clave: "valido",
      titulo: "Válido hasta",
      render: (c: Cotizacion) =>
        c.valido_hasta ? (
          <Row espacio={2} envolver>
            <span>{formatFecha(c.valido_hasta)}</span>
            {t.vencida(c) && <Badge tono="peligro">Vencido</Badge>}
          </Row>
        ) : (
          "—"
        ),
    },
    { clave: "adjuntos", titulo: "Adjuntos", render: (c: Cotizacion) => (c.cantidad_adjuntos > 0 ? String(c.cantidad_adjuntos) : "—") },
  ];

  // Los de antes sin subcategoría: con su etiqueta, para saber dónde ubicarlos.
  const columnasSin = [...columnas.slice(0, 2), { clave: "etiqueta", titulo: "Etiqueta", render: (c: Cotizacion) => (c.etiqueta ? <Badge>{c.etiqueta}</Badge> : "—") }, ...columnas.slice(2)];

  const nombre = (c: Cotizacion) => `${c.proveedor} (${textoSubcategoria(c)})`;
  const accionesAdmin = (c: Cotizacion) => (
    <>
      <Button variante="secundario" tamanio="sm" aria-label={`Editar ${nombre(c)}`} onClick={() => t.abrirEditar(c)}>
        Editar
      </Button>
      <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${nombre(c)}`} onClick={() => t.abrirBorrar(c)}>
        Borrar
      </Button>
    </>
  );
  const accionesLectura = (c: Cotizacion) =>
    c.cantidad_adjuntos > 0 ? (
      <Button variante="secundario" tamanio="sm" aria-label={`Ver adjuntos de ${nombre(c)}`} onClick={() => t.abrirAdjuntos(c)}>
        Ver adjuntos
      </Button>
    ) : null;

  const columnasComparar = [
    {
      clave: "proveedor",
      titulo: "Proveedor",
      render: (f: FilaComparacion) => (
        <Row espacio={2} envolver>
          <span>{f.cotizacion.proveedor}</span>
          {f.masConveniente && <Badge tono="exito">Más conveniente</Badge>}
          {f.masBarato && <Badge tono="primario">Más barato</Badge>}
          {f.aceptada && <Badge tono="primario">Aceptado</Badge>}
          {f.vencida && <Badge tono="peligro">Vencido</Badge>}
        </Row>
      ),
    },
    { clave: "monto", titulo: "Monto", render: (f: FilaComparacion) => <Monto c={f.cotizacion} /> },
    { clave: "usd", titulo: "En USD", render: (f: FilaComparacion) => formatMoneda(f.cotizacion.monto_usd ?? "0", "USD") },
    { clave: "diferencia", titulo: "Diferencia", render: (f: FilaComparacion) => (f.masBarato ? "—" : `+${formatMoneda(f.diferenciaUsd, "USD")}`) },
    { clave: "porcentaje", titulo: "%", render: (f: FilaComparacion) => (f.masBarato ? "—" : formatDiferenciaPorcentaje(f.diferenciaPorcentaje)) },
    {
      clave: "confianza",
      titulo: "Confianza",
      render: (f: FilaComparacion) =>
        f.cotizacion.proveedor_validado || f.cotizacion.proveedor_recomendado_por ? (
          <Row espacio={2} envolver>
            {f.cotizacion.proveedor_validado && <Badge tono="exito">Validado</Badge>}
            {f.cotizacion.proveedor_recomendado_por && <span>{`De parte de ${f.cotizacion.proveedor_recomendado_por}`}</span>}
          </Row>
        ) : (
          "—"
        ),
    },
    { clave: "puntaje", titulo: "Puntaje", render: (f: FilaComparacion) => <span className="font-semibold">{formatPuntaje(f.puntaje)}</span> },
  ];

  const adjuntosDe = (puedeEditar: boolean) =>
    t.cargandoAdjuntos ? (
      <div className="grid place-items-center py-4 text-primario">
        <Spinner />
      </div>
    ) : (
      <Adjuntos etiqueta="Archivos del presupuesto" adjuntos={t.adjuntos} rutaDescarga={t.rutaDescarga} puedeEditar={puedeEditar} onSubir={t.subirAdjunto} onBorrar={t.borrarAdjunto} subiendo={t.subiendo} error={t.errorAdjuntos} />
    );

  const ramaSubcategoria = (s: NodoSubcategoria) => {
    const clave = `s${s.id}`;
    const comparacion = t.comparacion(s.cotizaciones);
    return (
      <Desplegable
        key={s.id}
        titulo={
          <span>
            <span className="tabular-nums text-texto-suave">{s.numero}</span> {s.descripcion}
            <span className="ml-2 text-chico font-normal text-texto-suave">{s.aceptada ? `${textoCantidad(s.cotizaciones.length)} · aceptado: ${s.aceptada.proveedor}` : textoCantidad(s.cotizaciones.length)}</span>
          </span>
        }
        abiertoInicial={t.abierta(clave, s.etapa)}
        onCambio={(a) => t.alternar(clave, a)}
        tono={TONO_ETAPA[s.etapa]}
        className="shadow-none"
      >
        {t.abierta(clave, s.etapa) && (
          <Stack espacio={4}>
            <PasosSubcategoria m={t.manual} itemId={s.id} nombre={`${s.numero} ${s.descripcion}`} />
            {comparacion.length > 1 && (
              <Stack espacio={2}>
                <h3 className="font-semibold">{`Comparar: ${s.numero} ${s.descripcion}`}</h3>
                <p className="text-chico text-texto-suave">Los presupuestos con monto, del más conveniente al menos. Puntaje sobre 100: precio 60 (el más barato saca 60, uno el doble de caro 30), proveedor Validado 30 y De parte de alguien 10. Tocá uno para ver su ficha y aceptarlo; al aceptarlo, los demás de la subcategoría quedan como no elegidos.</p>
                <DataList etiqueta={`Comparación de ${s.numero} ${s.descripcion}`} columnas={columnasComparar} filas={comparacion} claveFila={(f) => f.cotizacion.id} alElegirFila={(f) => t.abrirFicha(f.cotizacion)} />
              </Stack>
            )}
            <DataList
              etiqueta={`Presupuestos de ${s.numero} ${s.descripcion}`}
              columnas={columnas}
              filas={s.cotizaciones}
              claveFila={(c) => c.id}
              alElegirFila={t.abrirFicha}
              acciones={t.esAdmin ? accionesAdmin : accionesLectura}
              vacio="Todavía no hay presupuestos de esta subcategoría."
            />
            {t.esAdmin && (
              <div>
                <Button variante="secundario" tamanio="sm" aria-label={`Nuevo presupuesto de ${s.numero} ${s.descripcion}`} onClick={() => t.abrirNuevo(s.id)}>
                  Nuevo presupuesto acá
                </Button>
              </div>
            )}
          </Stack>
        )}
      </Desplegable>
    );
  };

  return (
    <Stack espacio={6}>
      <Row justificar="entre" envolver espacio={3}>
        <h1 className="text-pantalla font-light tracking-[-0.03em]">Presupuestos recibidos</h1>
        {t.esAdmin && (
          <Row envolver espacio={2}>
            {t.puedeEscanear && (
              <SelectorArchivos etiqueta="Presupuesto a escanear (PDF o foto)" textoBoton={t.escaneando ? "Leyendo…" : "Escanear presupuesto"} accept={ACCEPT_ADJUNTO} deshabilitado={t.escaneando} onElegir={t.escanear} />
            )}
            <Button variante="secundario" onClick={t.edicion.alternarEdicion}>
              {t.edicion.editando ? "Listo" : "Editar categorías"}
            </Button>
            <Button variante="secundario" cargando={t.manual.cargandoSugeridos} onClick={() => void t.manual.cargarSugeridos()}>
              Cargar pasos sugeridos
            </Button>
            {t.manual.puedeCopiar && (
              <Button variante="secundario" onClick={t.manual.abrirCopiar}>
                Copiar de otro desarrollo
              </Button>
            )}
            <Button onClick={() => t.abrirNuevo()}>Nuevo presupuesto</Button>
          </Row>
        )}
      </Row>
      {t.escaneando && (
        <Row espacio={2}>
          <span className="text-primario">
            <Spinner />
          </span>
          <span className="text-chico text-texto-suave">Leyendo el presupuesto, tarda unos segundos…</span>
        </Row>
      )}
      <Alerta texto={t.errorEscanear} />
      {t.manual.aviso && <p className="text-chico text-exito">{t.manual.aviso}</p>}
      <Alerta texto={t.manual.errorSugeridos ?? t.manual.error} />

      <Row envolver espacio={3} alinear="fin">
        <TextField etiqueta="Buscar" placeholder="Proveedor o descripción" value={t.texto} onChange={(e) => t.setTexto(e.target.value)} contenedorClassName="min-w-56 flex-1" />
        <Select etiqueta="Estado" opciones={OPCIONES_FILTRO_ESTADO} valor={t.estado} onCambio={t.setEstado} className="min-w-40" />
      </Row>
      {t.nombreProveedorFiltro && (
        <Row espacio={2}>
          <Badge tono="primario" onQuitar={t.quitarProveedor} etiquetaQuitar="Quitar filtro de proveedor">{`Proveedor: ${t.nombreProveedorFiltro}`}</Badge>
        </Row>
      )}

      {t.cargando && (
        <div className="grid place-items-center py-10 text-primario">
          <Spinner />
        </div>
      )}
      {t.errorCarga && (
        <Stack espacio={3}>
          <Alerta texto={t.errorCarga} />
          <div>
            <Button variante="secundario" onClick={t.reintentar}>
              Reintentar
            </Button>
          </div>
        </Stack>
      )}

      {!t.cargando && !t.errorCarga && t.edicion.editando && <EdicionArbol e={t.edicion} categorias={t.arbol.categorias} abierta={t.abierta} alternar={t.alternar} />}

      {!t.cargando && !t.errorCarga && !t.edicion.editando && (
        <Stack espacio={2}>
          {t.arbol.categorias.length === 0 && t.arbol.sinSubcategoria.length === 0 && <p className="text-texto-suave">{t.vacio}</p>}
          {t.arbol.categorias.map((cat) => {
            const clave = `c${cat.id}`;
            return (
              <Desplegable
                key={cat.id}
                titulo={
                  <span>
                    <span className="tabular-nums text-texto-suave">{cat.numero}</span> {cat.nombre}
                    <span className="ml-2 text-chico font-normal text-texto-suave">{textoCantidad(cat.cantidad, cat.aceptadas)}</span>
                  </span>
                }
                abiertoInicial={t.abierta(clave, cat.etapa)}
                onCambio={(a) => t.alternar(clave, a)}
                tono={TONO_ETAPA[cat.etapa]}
              >
                {t.abierta(clave, cat.etapa) && (
                  <Stack espacio={2}>
                    {cat.subcategorias.length === 0 && (
                      <p className="text-chico text-texto-suave">{t.esAdmin ? "Todavía no tiene subcategorías: agregalas con «Editar categorías»." : "Todavía no tiene subcategorías."}</p>
                    )}
                    {cat.subcategorias.map(ramaSubcategoria)}
                  </Stack>
                )}
              </Desplegable>
            );
          })}
          {t.arbol.sinSubcategoria.length > 0 && (
            <Desplegable titulo={`Sin subcategoría · ${textoCantidad(t.arbol.sinSubcategoria.length)}`} abiertoInicial={t.abierta("sin")} onCambio={(a) => t.alternar("sin", a)}>
              {t.abierta("sin") && (
                <Stack espacio={3}>
                  <Alerta tono="alerta" texto={t.esAdmin ? "Son de antes de las subcategorías: abrí cada uno y elegí su subcategoría (en su ficha o en «Editar»)." : "Son de antes de las subcategorías: falta ubicarlos."} />
                  <DataList etiqueta="Presupuestos sin subcategoría" columnas={columnasSin} filas={t.arbol.sinSubcategoria} claveFila={(c) => c.id} alElegirFila={t.abrirFicha} acciones={t.esAdmin ? accionesAdmin : accionesLectura} />
                </Stack>
              )}
            </Desplegable>
          )}
          {t.hayFiltros && t.arbol.categorias.length === 0 && t.arbol.sinSubcategoria.length === 0 && <p className="text-texto-suave">{t.vacio}</p>}
        </Stack>
      )}

      <DialogosPasos m={t.manual} />

      <Dialog abierto={d?.tipo === "form"} onCambio={(abierto) => !abierto && t.cerrar()} titulo={d?.tipo === "form" && d.cotizacion ? "Editar presupuesto" : "Nuevo presupuesto"}>
        <form onSubmit={(e) => void t.guardar(e)} noValidate>
          <Stack espacio={4}>
            {t.escaneado && (
              <Stack espacio={2}>
                <p className="text-chico text-texto-suave">{`Completado con lo que se leyó de «${t.escaneado.archivo.name}». Revisá cada dato antes de guardar; el archivo queda adjunto al presupuesto.`}</p>
                <Alerta tono="alerta" texto={t.escaneado.escaneo.aviso} />
              </Stack>
            )}
            <Stack espacio={2}>
              <Combobox
                etiqueta="Proveedor"
                opciones={t.opcionesProveedorForm}
                onElegir={t.elegirProveedor}
                onCrear={t.crearProveedor}
                textoCrear={(texto) => `Agregar «${texto}» a la agenda`}
                placeholder="Escribí el nombre o tocá para ver la lista"
                deshabilitado={t.creandoProveedor}
              />
              {t.nombreProveedorForm && (
                <Row espacio={2}>
                  <Badge tono="primario" onQuitar={t.quitarProveedorForm} etiquetaQuitar={`Quitar proveedor ${t.nombreProveedorForm}`}>
                    {t.nombreProveedorForm}
                  </Badge>
                </Row>
              )}
              {t.proveedorLeidoNuevo && (
                <p className="text-chico">{`«${t.proveedorLeidoNuevo}» no está en la agenda: se agrega al guardar. Si es uno que ya tenés, elegilo arriba.`}</p>
              )}
              <Alerta texto={t.errorCrearProveedor ?? t.errores.proveedor_id} />
            </Stack>
            <Stack espacio={2}>
              <Select etiqueta="Subcategoría" opciones={t.opcionesSubcategoria} valor={t.form.item_id || undefined} onCambio={(v) => t.cambiar("item_id", v)} placeholder="Escribí el número, el nombre o la categoría" error={t.errores.item_id} />
              {t.etiquetaVieja && <p className="text-chico text-texto-suave">{`Antes estaba con la etiqueta «${t.etiquetaVieja}»: elegí su subcategoría.`}</p>}
            </Stack>
            <TextField etiqueta="Fecha" type="date" value={t.form.fecha} onChange={(e) => t.cambiar("fecha", e.target.value)} error={t.errores.fecha} />
            <Select etiqueta="Estado del presupuesto" opciones={t.opcionesEstadoForm} valor={t.form.estado} onCambio={(v) => t.cambiar("estado", v as EstadoCotizacion)} />
            <MontoField
              etiqueta="Monto"
              valor={t.form.monto}
              onCambio={(v) => t.cambiar("monto", v)}
              moneda={t.form.moneda}
              onCambioMoneda={(m) => t.cambiar("moneda", m)}
              error={t.errores.monto}
              ayuda={t.form.estado === "pedido" ? "Opcional mientras no llegue el presupuesto" : undefined}
            />
            <Select etiqueta="Qué cubre" opciones={OPCIONES_COMPOSICION} valor={t.form.composicion} onCambio={(v) => t.cambiar("composicion", v)} />
            {t.form.composicion === "mixto" && (
              <PercentField
                etiqueta="% de materiales"
                ayuda="Vacío: el de los ítems que cubre. Con él se parten sus pagos en el cash flow."
                valor={t.form.porcentaje_materiales}
                onCambio={(v) => t.cambiar("porcentaje_materiales", v)}
                error={t.errores.porcentaje_materiales}
              />
            )}
            <TextField etiqueta="Válido hasta" type="date" ayuda="Opcional" value={t.form.valido_hasta} onChange={(e) => t.cambiar("valido_hasta", e.target.value)} error={t.errores.valido_hasta} />
            <TextField etiqueta="Descripción" ayuda="Opcional" value={t.form.descripcion} onChange={(e) => t.cambiar("descripcion", e.target.value)} error={t.errores.descripcion} />
            <TextArea etiqueta="Notas" ayuda="Opcional" value={t.form.notas} onChange={(e) => t.cambiar("notas", e.target.value)} error={t.errores.notas} filas={3} />
            {t.escaneado && t.escaneado.escaneo.lineas.length > 0 && (
              <Stack espacio={2}>
                <Casilla
                  etiqueta={`Guardar también las ${t.escaneado.escaneo.lineas.length} líneas leídas`}
                  ayuda="Después las revisás, corregís y vinculás a los ítems del presupuesto desde la ficha"
                  marcado={t.guardarLineas}
                  onCambio={t.setGuardarLineas}
                />
                <ul aria-label="Líneas leídas" className="flex flex-col gap-1 text-chico">
                  {t.escaneado.escaneo.lineas.map((l, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="min-w-0 break-words">{l.descripcion}</span>
                      <span className="shrink-0 text-texto-suave">
                        {[formatCantidad(l), l.precio_unitario === null ? null : formatMoneda(l.precio_unitario, t.form.moneda)].filter((x) => x && x !== "—").join(" × ") || "—"}
                      </span>
                    </li>
                  ))}
                </ul>
              </Stack>
            )}
            {t.escaneado && t.escaneado.escaneo.cuotas.length > 0 && (
              <Stack espacio={2}>
                <Casilla
                  etiqueta={`Guardar también la forma de pago leída (${t.escaneado.escaneo.cuotas.length === 1 ? "1 pago" : `${t.escaneado.escaneo.cuotas.length} pagos`})`}
                  ayuda="Después la revisás y corregís desde la ficha"
                  marcado={t.guardarCuotas}
                  onCambio={t.setGuardarCuotas}
                />
                <ul aria-label="Forma de pago leída" className="flex flex-col gap-1 text-chico">
                  {t.escaneado.escaneo.cuotas.map((c, i) => (
                    <li key={i} className="flex justify-between gap-3">
                      <span className="min-w-0 break-words">{c.concepto}</span>
                      <span className="shrink-0 text-texto-suave">{[formatPorcentajeCuota(c.porcentaje), c.fecha ? formatFecha(c.fecha) : null].filter(Boolean).join(" · ")}</span>
                    </li>
                  ))}
                </ul>
              </Stack>
            )}
            {d?.tipo === "form" && d.cotizacion ? adjuntosDe(true) : !t.escaneado && <p className="text-chico text-texto-suave">El PDF del presupuesto se adjunta después de guardarlo, desde «Editar».</p>}
            <Alerta texto={t.errorGuardar} />
            <Row justificar="fin" espacio={2}>
              <Button variante="secundario" onClick={t.cerrar}>
                Cancelar
              </Button>
              <Button type="submit" cargando={t.guardando}>
                Guardar
              </Button>
            </Row>
          </Stack>
        </form>
      </Dialog>

      <Dialog abierto={t.duplicado !== null} onCambio={(abierto) => !abierto && t.cerrar()} titulo={t.duplicado?.titulo ?? ""}>
        <Stack espacio={4}>
          <p>{t.duplicado?.texto}</p>
          <Row justificar="fin" espacio={2} envolver>
            {t.duplicado?.cargarIgual ? (
              <Button variante="secundario" onClick={t.cargarIgual}>
                Cargarlo igual
              </Button>
            ) : (
              <Button variante="secundario" onClick={t.cerrar}>
                Cerrar
              </Button>
            )}
            <Button onClick={t.verDuplicado}>Ver presupuesto</Button>
          </Row>
        </Stack>
      </Dialog>

      <Dialog abierto={d?.tipo === "adjuntos"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Adjuntos del presupuesto">
        <Stack espacio={4}>
          {d?.tipo === "adjuntos" && <p>{nombre(d.cotizacion)}</p>}
          {adjuntosDe(false)}
          <Row justificar="fin">
            <Button onClick={t.cerrar}>Cerrar</Button>
          </Row>
        </Stack>
      </Dialog>

      <DialogosCategorias e={t.edicion} />

      <Dialog abierto={d?.tipo === "borrar"} onCambio={(abierto) => !abierto && t.cerrar()} titulo="Borrar presupuesto">
        <Stack espacio={4}>
          <p>{t.mensajeBorrado}</p>
          <Alerta texto={t.errorBorrar} />
          <Row justificar="fin" espacio={2}>
            <Button variante="secundario" onClick={t.cerrar}>
              Cancelar
            </Button>
            <Button variante="peligro" cargando={t.borrando} onClick={() => void t.confirmarBorrar()}>
              Borrar
            </Button>
          </Row>
        </Stack>
      </Dialog>
    </Stack>
  );
}
